import React, { useEffect, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useParsedQuery } from 'components/hooks/useParsedQuery';
import { useSelector } from 'react-redux';
import { useCurrentProvider } from 'reducers/reviewResultSlice';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { acknowledgeReview } from 'services/performanceReview';

import { Button } from 'uiToolkitV1/Button';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { Paper } from 'uiToolkitV1/Paper';

import { useReviewFormSidebarData } from '../components/Sidebar/hooks/dataHooks';
import useReviewResultParams from '../hooks/useReviewResultParams';

const getAcknowledgementStatus = ({
  reviewInfo,
  isFormPublished,
  isManagerFormAcknowledged,
  loggedInEmployee,
  selectedProvider,
}) =>
  isFormPublished &&
  !isManagerFormAcknowledged &&
  reviewInfo.receiverEmployeeId == loggedInEmployee.id && // using == js type is different for some reason
  selectedProvider?.providerType === REVIEW_PROVIDER_TYPES.MANAGER;

const RabbitResultAcknowledge = () => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const queryClient = useQueryClient();

  const [agreeConfirmation, setAgreeConfirmation] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const { employeeId, reviewId, providerId } = useReviewResultParams();
  const { data: formData = {} } = useReviewFormSidebarData({ employeeId, providerId, reviewId });
  const { reviewInfo, isFormPublished, isManagerFormAcknowledged } = formData;
  const { autoNavigate = false } = useParsedQuery();
  const [isDoneAutoNavigation, setIsDoneAutoNavigation] = useState(false);

  const selectedProvider = useCurrentProvider();

  const acceptReview = () => {
    acknowledgeReview({
      reviewId,
      providerId: selectedProvider.id,
    })
      .then(() => {
        setIsAcknowledged(true);
        queryClient.invalidateQueries({
          queryKey: [REACT_QUERY_KEYS.ReviewFormSidebar, employeeId, reviewId, providerId],
        });
      })
      .catch(() => {})
      .finally(() => {});
  };

  const showAcknowledgeFooter = getAcknowledgementStatus({
    reviewInfo,
    isFormPublished,
    isManagerFormAcknowledged,
    loggedInEmployee,
    selectedProvider,
  });

  useEffect(() => {
    if (!showAcknowledgeFooter || isAcknowledged) return;
    if (autoNavigate && !isDoneAutoNavigation) {
      const buttonElement = document.getElementsByClassName('review-form-action-button')[0];
      if (!buttonElement) {
        return;
      }
      setTimeout(() => {
        buttonElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 2000);
      setIsDoneAutoNavigation(true);
    }
  }, [showAcknowledgeFooter, isAcknowledged]);

  if (!showAcknowledgeFooter || isAcknowledged) return null;

  return (
    <Paper mt="16px" p="16px" boxShadow="0px 0px 2px rgba(0, 0, 0, 0.25)">
      <Flex justifyContent="space-between" bg="surface.white">
        <Checkbox isChecked={agreeConfirmation} onChange={() => setAgreeConfirmation((val) => !val)}>
          Your manager has shared this feedback with you, by submitting this you acknowledge this feedback which was
          given
        </Checkbox>
        <Button disabled={!agreeConfirmation} px={6} onClick={acceptReview} className="review-form-action-button">
          Confirm
        </Button>
      </Flex>
    </Paper>
  );
};

export default RabbitResultAcknowledge;
